import * as R from 'ramda'
import { useCallback, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  AppBar,
  Checkbox,
  Divider,
  Radio,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import { LIMITS } from '@weblite/permission'
import { BackButton, PrimaryButton } from '$common/buttons'
import { MultilineInput } from '$common/input'
import { closePremiumFeaturesRequestForm } from '$logic/router'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { getClientUser } from '$logic/client-user'
import { t } from '$i18n'
import { setLastPremiumFeaturesRequestTime } from '$logic/payment'
import { showRequestForPremiumPlanSentSuccessfully } from '$comp/snackbars'

const { userConstraints } = LIMITS

type Role = 'TEACHER' | 'INSTITUTE' | 'SCHOOL'

const ROLES_LIST = ['TEACHER', 'INSTITUTE', 'SCHOOL']

type TUserPlan =
  | 'ONLINE_CLASS'
  | 'PRIVATE_CLASS'
  | 'OFFLINE_CLASS'
  | 'SCHOOL_ONLINE_CLASS'

const PLANS_LIST = [
  'ONLINE_CLASS',
  'PRIVATE_CLASS',
  'OFFLINE_CLASS',
  'SCHOOL_ONLINE_CLASS',
] as TUserPlan[]

export function PremiumFeaturesRequestForm() {
  const [role, setRole] = useState<Role | null>(null)

  const [userPlans, setUserPlans] = useState<TUserPlan[]>([])

  const toggleUserPlan = useCallback((plan: TUserPlan) => {
    setUserPlans(oldPlans =>
      oldPlans.includes(plan)
        ? R.without([plan], oldPlans)
        : [...oldPlans, plan],
    )
  }, [])

  const [description, setDescription] = useState<string>('')

  const onSubmit = useCallback(() => {
    analite(
      'REQUEST_PREMIUM_PLAN',
      {
        phone: getClientUser()?.phone,
        firstname: getClientUser()?.firstname,
        lastname: getClientUser()?.lastname,
        role,
        plans: userPlans.join('-'),
        description,
      },
      true,
    )
    setLastPremiumFeaturesRequestTime()
    closePremiumFeaturesRequestForm()
    showRequestForPremiumPlanSentSuccessfully()
  }, [role, userPlans, description])
  return (
    <PageWithAppBar
      bodySx={{ backgroundColor: 'neutral000' }}
      appBar={
        <AppBarBackTitle
          title={t('premiumFeatureRequestForm.title')}
          onBack={closePremiumFeaturesRequestForm}
        />
      }
    >
      <UserRole role={role} changeRole={setRole} />
      <Divider />

      <UserPlanList userPlans={userPlans} setUserPlans={toggleUserPlan} />
      <Divider sx={{ marginTop: '12px' }} />

      <ContentWrapper>
        <Typography marginTop="12px" variant="subtitle3" color="neutral1000">
          {t('premiumFeatureRequestForm.askUs')}
        </Typography>
        <MultilineInput
          sx={{ marginTop: '16px' }}
          autoFocus
          maxLength={userConstraints.bio.maxLength}
          value={description}
          placeholder={t('premiumFeatureRequestForm.description')}
          onChange={setDescription}
        />
      </ContentWrapper>

      <ButtonWrapper>
        <PrimaryButton
          disabled={!role || !userPlans.length}
          fullWidth
          sx={{ marginTop: '16px', minHeight: '48px' }}
          text={t('premiumFeatureRequestForm.submit')}
          onClick={onSubmit}
        />
      </ButtonWrapper>
    </PageWithAppBar>
  )
}

const ButtonWrapper = styled('div')`
  padding: 16px;
  margin-top: auto;
`

const ContentWrapper = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`

function AppBarBackTitle({
  title,
  onBack,
}: {
  title: string
  onBack: () => void
}) {
  return (
    <AppBar sx={{ maxWidth: '100%', top: 0 }}>
      <PageToolbar>
        <BackButton onClick={onBack} />
        <Typography marginRight="8px" color="neutral900" variant="subtitle1">
          {title}
        </Typography>
      </PageToolbar>
    </AppBar>
  )
}

const PageToolbar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: theme.palette.neutral100,
  borderBottom: '1px solid',
  borderColor: '#0000001A',
  height: '56px',
}))

function UserRole({
  role,
  changeRole,
}: {
  role: Role | null
  changeRole: (role: Role) => void
}) {
  return (
    <ContentWrapper>
      <Typography marginTop="16px" variant="subtitle3" color="neutral1000">
        {t('premiumFeatureRequestForm.whoAreYou.title')}
      </Typography>
      <div
        style={{ marginTop: '8px', display: 'flex', flexDirection: 'column' }}
      >
        {(ROLES_LIST as Role[]).map(itemRole => (
          <UserRoleItem
            key={itemRole}
            itemRole={itemRole}
            checked={role === itemRole}
            onChange={changeRole}
          />
        ))}
      </div>
    </ContentWrapper>
  )
}

function UserRoleItem({
  onChange,
  checked,
  itemRole,
}: {
  checked: boolean
  itemRole: Role
  onChange: (role: Role) => void
}) {
  return (
    <Stack direction="row" gap="8px" height="40px" alignItems="center">
      <Radio
        checked={checked}
        onChange={() => onChange(itemRole)}
        sx={{
          color: 'neutral400',
          '&.Mui-checked': {
            color: 'secondary500',
          },
          padding: '2px',
        }}
      />
      <Typography color="neutral900" variant="body2">
        {t(`premiumFeatureRequestForm.whoAreYou.${itemRole}`)}
      </Typography>
    </Stack>
  )
}

function UserPlanList({
  userPlans,
  setUserPlans,
}: {
  userPlans: TUserPlan[]
  setUserPlans: (plan: TUserPlan) => void
}) {
  return (
    <ContentWrapper>
      <Typography marginTop="12px" variant="subtitle3" color="neutral1000">
        {t('premiumFeatureRequestForm.yourPlan.title')}
      </Typography>
      <div
        style={{ marginTop: '8px', display: 'flex', flexDirection: 'column' }}
      >
        {PLANS_LIST.map(item => (
          <UserPlanItem
            key={item}
            planItem={item}
            onToggle={setUserPlans}
            checked={userPlans.includes(item)}
          />
        ))}
      </div>
    </ContentWrapper>
  )
}

function UserPlanItem({
  planItem,
  onToggle,
  checked,
}: {
  planItem: TUserPlan
  onToggle: (plan: TUserPlan) => void
  checked: boolean
}) {
  return (
    <Stack direction="row" gap="8px" height="40px" alignItems="center">
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(planItem)}
        sx={{
          color: 'neutral400',
          '&.Mui-checked': {
            color: 'secondary500',
          },
          // '.MuiCheckbox-root': {
          padding: '2px',
          // },
        }}
      />
      <Typography color="neutral900" variant="body2">
        {t(`premiumFeatureRequestForm.yourPlan.${planItem}`)}
      </Typography>
    </Stack>
  )
}
